import { useRouter } from 'next/router';

import { Alert, Box, Heading, Paragraph, TextButton } from '@zillow/constellation-10';

import {
  useRentalManagerPropertyAddressRentZestimateWebApiHandler_rentZestimate,
  useRentalManagerUserFreemiumAnalyticsWebApiHandler_pricingGuidance,
  useRentalManagerUserFreemiumWebApiHandler_isTopOfFunnelEnrollmentEligible,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import accessUtils from '../../../../utils/accessUtils';
import { getCookieAsBoolean } from '../../../../utils/cookieUtils';
import numberUtils from '../../../../utils/numberUtils';
import propertyUtils from '../../../../utils/propertyUtils';

import { ZRM_USE_FREEMIUM_TEST_DATA } from '../../../../constants/FeatureFlagConstants';
import { PRODUCT_TYPE_NAME_TO_ID } from '../../../../constants/FreemiumConstants';

import PremiumPricingGuidanceModal from '../../../modals/PremiumPricingGuidanceModal';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';

import MonthlyRentFormField from '../../../PostingPath/MonthlyRentFormField';
import SecurityDepositFormField from '../../../PostingPath/SecurityDepositFormField';
import SpecialOffers from '../../../PostingPath/SpecialOffers';
import { PremiumAnalyticsProvider } from '../../../PremiumAnalytics/PremiumAnalyticsContext';
import PageWizard from '../../../Wizard/PageWizard';
import WizardTipCardContainer from '../../../WizardTipCardContainer';
import PremiumPriceTipContent from './PremiumPriceTipContent';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const MonthlyRentAndSecurityDeposit = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const isActiveProductTypePremium = listingDetails?.freemium?.activeProduct?.type === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;

  const { data: isTopOfFunnelEnrollmentEligible = false } =
    useRentalManagerUserFreemiumWebApiHandler_isTopOfFunnelEnrollmentEligible({
      enabled: !isActiveProductTypePremium,
      meta: { tags: ['isTopOfFunnelEnrollmentEligible'], context: { api: 'freemium', details: 'ListingPage' } },
    });

  const canAccessTopOfFunnelPremium = accessUtils.canAccessTopOfFunnelPremium({
    isFreemiumEnabled: listingDetails?.freemium?.isEnabled ?? false,
    isTopOfFunnelEnrollmentEligible,
    isActiveProductTypePremium,
  });

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false,
  });

  const formattedCompleteAddress = propertyUtils.formatAddress({
    street: listingDetails?.street,
    city: listingDetails?.city,
    state: listingDetails?.state,
    zip: listingDetails?.zip,
  }).formattedCompleteAddress;

  const {
    refetch: refetchPremiumPricingGuidance,
    data: pricingGuidance,
    error: pricingGuidanceError,
  } = useRentalManagerUserFreemiumAnalyticsWebApiHandler_pricingGuidance(
    { listingAlias, useTestData: getCookieAsBoolean(ZRM_USE_FREEMIUM_TEST_DATA) },
    {
      enabled: Boolean(listingAlias && canAccessTopOfFunnelPremium && isActiveProductTypePremium),
    },
  );

  const { data: rentZestimateData } = useRentalManagerPropertyAddressRentZestimateWebApiHandler_rentZestimate(
    { fullAddress: formattedCompleteAddress, unit: listingDetails?.unit },
    { enabled: !!formattedCompleteAddress },
  );

  const header = 'About Rent Zestimate®';
  let premiumHeader = `Optimize your listing with ${listingDetails?.city} pricing data and insights`;

  if (isActiveProductTypePremium && pricingGuidance && pricingGuidance?.marketMedian?.price) {
    const {
      beds,
      marketMedian: { price: medianPrice },
      propertyType: { display: propertyTypeDisplay },
      zip,
    } = pricingGuidance;
    if ((beds || beds === 0) && propertyTypeDisplay && zip && medianPrice) {
      if (beds === 0) {
        premiumHeader = `The median rental price for studios in ${zip} is ${numberUtils.asCurrency(Number(numberUtils.removeDollarSign(medianPrice)))}`;
      } else {
        premiumHeader = `The median rental price for ${beds} bedroom ${propertyTypeDisplay.toLowerCase()}s in ${zip} is ${numberUtils.asCurrency(Number(numberUtils.removeDollarSign(medianPrice)))}`;
      }
    }
  }

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep || undefined}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      {...rest}
    >
      <Box css={wizardFlexContainerStyles}>
        <Box data-testid="FormSectionContainer" css={wizardFormSectionContainerStyles.raw()}>
          <Heading textStyle="heading-sm" level={2} css={wizardHeadingStyles}>
            {canAccessTotalMonthlyPriceWithFee
              ? 'How much are the base rent and the security deposit?'
              : 'How much are the monthly rent and the security deposit?'}
          </Heading>

          <Paragraph css={wizardParagraphStyles}>
            A security deposit isn’t required, but you can choose to collect one.
          </Paragraph>

          <MonthlyRentFormField />

          <SecurityDepositFormField />

          <Box css={{ marginTop: '800' }}>
            <SpecialOffers />
          </Box>
        </Box>

        {canAccessTopOfFunnelPremium ? (
          <WizardTipCardContainer header={premiumHeader} isPremiumTip={true}>
            {pricingGuidanceError ? (
              <Alert
                tone="warning"
                actionButton={<TextButton onClick={() => refetchPremiumPricingGuidance()}>Retry now</TextButton>}
              >
                We are having issues loading your pricing guidance data
              </Alert>
            ) : (
              <PremiumAnalyticsProvider>
                <PremiumPriceTipContent
                  isActiveProductTypePremium={isActiveProductTypePremium}
                  pricingGuidance={pricingGuidance || null}
                />
              </PremiumAnalyticsProvider>
            )}
          </WizardTipCardContainer>
        ) : (
          !!rentZestimateData?.rentZestimate && (
            <WizardTipCardContainer header={header}>
              <Paragraph>
                A Rent Zestimate® is Zillow’s estimated monthly rental price, computed using a proprietary formula. It
                is a starting point in determining the monthly rental price for a specific property.
              </Paragraph>
            </WizardTipCardContainer>
          )
        )}
      </Box>
      {canAccessTopOfFunnelPremium && isActiveProductTypePremium && (
        <PremiumAnalyticsProvider>
          <PremiumPricingGuidanceModal />
        </PremiumAnalyticsProvider>
      )}
    </PageWizard>
  );
};

export default MonthlyRentAndSecurityDeposit;
